import { computed, shallowRef } from 'vue'
import { fetchMessageHistory, type MessageHistoryItem } from '@/api/messages'
import { useUserStore } from '@/stores/user'

export type MessageHistoryFilter = 'all' | 'unread' | 'failed'
export type MessageHistoryStatusTone = 'error' | 'pending' | 'working' | 'success'
export type MessageHistoryContentTone = 'reply' | 'narration' | 'system'

interface MessageSegment {
  kind: MessageHistoryContentTone
  speaker: string
  text: string
}

export interface MessageHistoryEntry {
  id: string
  pipeId: string
  imageUrl: string | null
  title: string
  status: number
  statusLabel: string
  statusTone: MessageHistoryStatusTone
  statusDescription: string
  useNum: number
  useNumLabel: string
  useNumShortLabel: string
  heatLabel: string
  createdTime: string | null
  updatedTime: string | null
  latestLine: string
  latestLineWithSpeaker: string
  previewText: string
  spotlightSummary: string
  speakerName: string
  contentTone: MessageHistoryContentTone
  contentToneLabel: string
  choiceLabel: string | null
  cartoonId: string | null
  cartoonName: string
  cartoonAvatar: string | null
  isTop: boolean
  unreadCount: number
  hasUnread: boolean
  dayKey: string
  dayLabel: string
  timeLabel: string
  updatedLabel: string
  absoluteDateLabel: string
  sortTimestamp: number
}

export interface MessageHistoryGroup {
  key: string
  label: string
  items: MessageHistoryEntry[]
}

function parseTimeToMs(value: string | null | undefined) {
  if (!value) {
    return 0
  }

  const timestamp = Date.parse(value)
  return Number.isFinite(timestamp) ? timestamp : 0
}

function startOfDay(timestamp: number) {
  if (!timestamp) {
    return 0
  }

  const date = new Date(timestamp)
  date.setHours(0, 0, 0, 0)
  return date.getTime()
}

function formatDayLabel(timestamp: number) {
  if (!timestamp) {
    return '更早'
  }

  const today = startOfDay(Date.now())
  const target = startOfDay(timestamp)
  const delta = Math.round((today - target) / 86400000)

  if (delta === 0) {
    return '今天'
  }

  if (delta === 1) {
    return '昨天'
  }

  const date = new Date(timestamp)
  const currentYear = new Date().getFullYear()

  if (date.getFullYear() === currentYear) {
    return new Intl.DateTimeFormat('zh-CN', {
      month: 'long',
      day: 'numeric',
      weekday: 'short',
    }).format(date)
  }

  return new Intl.DateTimeFormat('zh-CN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date)
}

function formatClock(timestamp: number) {
  if (!timestamp) {
    return '刚刚'
  }

  return new Intl.DateTimeFormat('zh-CN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(timestamp))
}

function formatAbsoluteDate(timestamp: number) {
  if (!timestamp) {
    return '时间未知'
  }

  return new Intl.DateTimeFormat('zh-CN', {
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(timestamp))
}

function formatUpdatedLabel(timestamp: number) {
  if (!timestamp) {
    return '等待更新'
  }

  const diff = Date.now() - timestamp

  if (diff < 3600000) {
    const minutes = Math.max(1, Math.round(diff / 60000))
    return `${minutes} 分钟前有新剧情`
  }

  if (diff < 86400000) {
    const hours = Math.max(1, Math.round(diff / 3600000))
    return `${hours} 小时前有新剧情`
  }

  const days = Math.max(1, Math.round(diff / 86400000))
  return `${days} 天前有新剧情`
}

function formatUseNum(value: number) {
  if (value >= 10000) {
    const numeric = value >= 100000 ? (value / 10000).toFixed(0) : (value / 10000).toFixed(1)
    return `${numeric} 万互动`
  }

  return `${Math.max(0, value)} 次互动`
}

function formatUseNumShort(value: number) {
  if (value >= 100000000) {
    return `${(value / 100000000).toFixed(1)} 亿`
  }

  if (value >= 10000) {
    const numeric = value >= 100000 ? (value / 10000).toFixed(0) : (value / 10000).toFixed(1)
    return `${numeric} 万`
  }

  return String(Math.max(0, value))
}

function resolveHeatLabel(value: number) {
  if (value >= 3000000) {
    return '高热剧情'
  }

  if (value >= 500000) {
    return '持续热聊'
  }

  if (value >= 100000) {
    return '讨论升温'
  }

  return '可继续阅读'
}

function stripMarkup(text: string) {
  return text
    .replace(/<[^>]+>/g, '')
    .replace(/\r?\n+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function parseRoleName(value: unknown) {
  if (typeof value !== 'string' || !value.trim().startsWith('{')) {
    return ''
  }

  try {
    const parsed = JSON.parse(value) as Record<string, unknown>
    return String(parsed.nickname || parsed.name || '').trim()
  } catch {
    return ''
  }
}

function resolveSegmentKind(block: Record<string, unknown>) {
  if (block.role && typeof block.role === 'object') {
    return 'reply' as const
  }

  const ui = block.ui
  const uiName =
    ui && typeof ui === 'object' ? String((ui as Record<string, unknown>).name || '') : ''

  if (uiName === 'narration') {
    return 'narration' as const
  }

  return 'system' as const
}

function extractEventLabel(eventValue: unknown) {
  if (!Array.isArray(eventValue)) {
    return null
  }

  for (const eventItem of eventValue) {
    if (!eventItem || typeof eventItem !== 'object') {
      continue
    }

    const value = stripMarkup(String((eventItem as Record<string, unknown>).val || ''))

    if (!value || /^https?:\/\//.test(value) || value === 'true' || value === 'false') {
      continue
    }

    if (value.length > 28) {
      continue
    }

    return value
  }

  return null
}

function extractSegments(contentJson: unknown[] | null) {
  const segments: MessageSegment[] = []
  let choiceLabel: string | null = null

  if (!Array.isArray(contentJson)) {
    return { segments, choiceLabel }
  }

  contentJson.forEach((block) => {
    if (!block || typeof block !== 'object') {
      return
    }

    const record = block as Record<string, unknown>
    const kind = resolveSegmentKind(record)
    const speaker =
      kind === 'reply'
        ? parseRoleName((record.role as Record<string, unknown> | undefined)?.val)
        : ''

    if (!choiceLabel) {
      choiceLabel = extractEventLabel(record.event)
    }

    const content = Array.isArray(record.content) ? record.content : []

    content.forEach((item) => {
      if (!item || typeof item !== 'object') {
        return
      }

      const text = stripMarkup(String((item as Record<string, unknown>).val || ''))

      if (!text) {
        return
      }

      segments.push({
        kind,
        speaker,
        text,
      })
    })
  })

  return { segments, choiceLabel }
}

function resolveContentToneLabel(kind: MessageHistoryContentTone) {
  switch (kind) {
    case 'reply':
      return '角色回信'
    case 'narration':
      return '剧情推进'
    default:
      return '系统片段'
  }
}

function resolveLatestContent(contentJson: unknown[] | null, status: number, cartoonName: string) {
  const { segments, choiceLabel } = extractSegments(contentJson)
  const latestSegment = segments[segments.length - 1] ?? null

  if (latestSegment) {
    const speakerName = latestSegment.speaker || (latestSegment.kind === 'reply' ? cartoonName : '')
    const latestLine = latestSegment.text
    const latestLineWithSpeaker = speakerName ? `${speakerName}：${latestLine}` : latestLine

    return {
      latestLine,
      latestLineWithSpeaker,
      previewText: latestLineWithSpeaker,
      spotlightSummary: latestLine,
      speakerName,
      contentTone: latestSegment.kind,
      contentToneLabel: resolveContentToneLabel(latestSegment.kind),
      choiceLabel,
    }
  }

  switch (status) {
    case 0:
      return {
        latestLine: '这条互动在生成过程中断开了。',
        latestLineWithSpeaker: '这条互动在生成过程中断开了。',
        previewText: '这条互动在生成过程中断开了。',
        spotlightSummary: '返回场景查看失败原因或继续重试。',
        speakerName: '',
        contentTone: 'system' as const,
        contentToneLabel: '异常状态',
        choiceLabel,
      }
    case 1:
      return {
        latestLine: '已经进入队列，稍后会继续回复你。',
        latestLineWithSpeaker: '已经进入队列，稍后会继续回复你。',
        previewText: '已经进入队列，稍后会继续回复你。',
        spotlightSummary: '新的回复还在路上。',
        speakerName: '',
        contentTone: 'system' as const,
        contentToneLabel: '等待开始',
        choiceLabel,
      }
    case 2:
      return {
        latestLine: '新的剧情和回应正在生成中。',
        latestLineWithSpeaker: '新的剧情和回应正在生成中。',
        previewText: '新的剧情和回应正在生成中。',
        spotlightSummary: '回到场景可以继续推进互动。',
        speakerName: '',
        contentTone: 'system' as const,
        contentToneLabel: '处理中',
        choiceLabel,
      }
    default:
      return {
        latestLine: '这条消息已经抵达，点击继续进入。',
        latestLineWithSpeaker: '这条消息已经抵达，点击继续进入。',
        previewText: '这条消息已经抵达，点击继续进入。',
        spotlightSummary: '回到场景继续阅读这段故事。',
        speakerName: '',
        contentTone: 'system' as const,
        contentToneLabel: '已更新',
        choiceLabel,
      }
  }
}

function resolveStatusMeta(status: number) {
  switch (status) {
    case 0:
      return {
        label: '生成失败',
        tone: 'error' as const,
        description: '异常中断',
      }
    case 1:
      return {
        label: '等待开始',
        tone: 'pending' as const,
        description: '排队中',
      }
    case 2:
      return {
        label: '处理中',
        tone: 'working' as const,
        description: '剧情生成中',
      }
    case 3:
      return {
        label: '最新回信',
        tone: 'success' as const,
        description: '继续阅读',
      }
    default:
      return {
        label: '状态更新',
        tone: 'pending' as const,
        description: '已更新',
      }
  }
}

function normalizeMessage(item: MessageHistoryItem): MessageHistoryEntry {
  const sortTimestamp = parseTimeToMs(item.updatedTime) || parseTimeToMs(item.createdTime)
  const statusMeta = resolveStatusMeta(item.status)
  const unreadCount = Math.max(0, Number(item.unRead || 0))
  const cartoonName = item.cartoonName?.trim() || '造梦角色'
  const latestContent = resolveLatestContent(item.contentJson, item.status, cartoonName)

  return {
    id: item.id,
    pipeId: item.pipeId,
    imageUrl: item.img || null,
    title: item.title?.trim() || '未命名消息',
    status: item.status,
    statusLabel: statusMeta.label,
    statusTone: statusMeta.tone,
    statusDescription: statusMeta.description,
    useNum: item.useNum,
    useNumLabel: formatUseNum(item.useNum),
    useNumShortLabel: formatUseNumShort(item.useNum),
    heatLabel: resolveHeatLabel(item.useNum),
    createdTime: item.createdTime,
    updatedTime: item.updatedTime,
    latestLine: latestContent.latestLine,
    latestLineWithSpeaker: latestContent.latestLineWithSpeaker,
    previewText: latestContent.previewText,
    spotlightSummary: latestContent.spotlightSummary,
    speakerName: latestContent.speakerName,
    contentTone: latestContent.contentTone,
    contentToneLabel: latestContent.contentToneLabel,
    choiceLabel: latestContent.choiceLabel,
    cartoonId: item.cartoonId,
    cartoonName,
    cartoonAvatar: item.cartoonAvatar || null,
    isTop: item.top === 1,
    unreadCount,
    hasUnread: unreadCount > 0,
    dayKey: String(startOfDay(sortTimestamp) || 'older'),
    dayLabel: formatDayLabel(sortTimestamp),
    timeLabel: formatClock(sortTimestamp),
    updatedLabel: formatUpdatedLabel(sortTimestamp),
    absoluteDateLabel: formatAbsoluteDate(sortTimestamp),
    sortTimestamp,
  }
}

function mergeUniqueMessages(current: MessageHistoryEntry[], incoming: MessageHistoryEntry[]) {
  const byId = new Map(current.map((item) => [item.id, item]))

  incoming.forEach((item) => {
    byId.set(item.id, item)
  })

  return Array.from(byId.values()).sort((left, right) => {
    if (left.isTop !== right.isTop) {
      return Number(right.isTop) - Number(left.isTop)
    }

    if (left.status !== right.status) {
      return left.status === 0 ? 1 : right.status === 0 ? -1 : 0
    }

    return right.sortTimestamp - left.sortTimestamp
  })
}

function filterItems(items: MessageHistoryEntry[], filter: MessageHistoryFilter) {
  switch (filter) {
    case 'unread':
      return items.filter((item) => item.hasUnread)
    case 'failed':
      return items.filter((item) => item.status === 0)
    default:
      return items
  }
}

function buildGroups(items: MessageHistoryEntry[]) {
  const groups: MessageHistoryGroup[] = []

  items.forEach((item) => {
    const lastGroup = groups[groups.length - 1]

    if (lastGroup?.key === item.dayKey) {
      lastGroup.items.push(item)
      return
    }

    groups.push({
      key: item.dayKey,
      label: item.dayLabel,
      items: [item],
    })
  })

  return groups
}

export function useMessageHistory() {
  const userStore = useUserStore()
  const items = shallowRef<MessageHistoryEntry[]>([])
  const loadingInitial = shallowRef(false)
  const loadingMore = shallowRef(false)
  const error = shallowRef('')
  const nextPageNo = shallowRef(1)
  const hasNext = shallowRef(true)
  const eventId = shallowRef<string | null>(null)
  let requestId = 0

  const totalCount = computed(() => items.value.length)
  const unreadThreadCount = computed(() => items.value.filter((item) => item.hasUnread).length)
  const unreadPulseCount = computed(() =>
    items.value.reduce((sum, item) => sum + item.unreadCount, 0),
  )
  const failedCount = computed(() => items.value.filter((item) => item.status === 0).length)
  const pinnedCount = computed(() => items.value.filter((item) => item.isTop).length)
  const canLoadMore = computed(() => hasNext.value && !loadingInitial.value && !loadingMore.value)

  function reset() {
    items.value = []
    error.value = ''
    nextPageNo.value = 1
    hasNext.value = false
    eventId.value = null
  }

  function getUid() {
    return userStore.getUid() || userStore.getId()
  }

  async function loadPage(pageNo: number, mode: 'replace' | 'append') {
    if (!userStore.isLoggedIn()) {
      reset()
      return false
    }

    const uid = getUid()

    if (!uid) {
      throw new Error('缺少用户标识，暂时无法加载消息')
    }

    const response = await fetchMessageHistory(uid, pageNo)

    if (response.code === 302) {
      userStore.resetUserInfo()
      reset()
      return false
    }

    if (response.code !== 0) {
      throw new Error(response.msg || '消息加载失败')
    }

    const normalized = (response.data?.list ?? []).map(normalizeMessage)
    items.value =
      mode === 'replace'
        ? mergeUniqueMessages([], normalized)
        : mergeUniqueMessages(items.value, normalized)
    eventId.value = response.data?.eventId ?? null
    nextPageNo.value = pageNo + 1
    hasNext.value = normalized.length > 0
    return true
  }

  async function refresh() {
    const currentRequestId = ++requestId
    loadingInitial.value = true
    error.value = ''
    nextPageNo.value = 1
    hasNext.value = true

    try {
      await loadPage(1, 'replace')
    } catch (err) {
      if (currentRequestId !== requestId) {
        return
      }

      items.value = []
      error.value = err instanceof Error ? err.message : '消息加载失败，请稍后再试'
    } finally {
      if (currentRequestId === requestId) {
        loadingInitial.value = false
      }
    }
  }

  async function fetchNext() {
    if (!canLoadMore.value) {
      return
    }

    const currentRequestId = ++requestId
    loadingMore.value = true

    try {
      await loadPage(nextPageNo.value, 'append')
    } catch (err) {
      if (currentRequestId === requestId) {
        error.value = err instanceof Error ? err.message : '更多消息加载失败'
        hasNext.value = false
      }
    } finally {
      if (currentRequestId === requestId) {
        loadingMore.value = false
      }
    }
  }

  function getFilteredItems(filter: MessageHistoryFilter) {
    return filterItems(items.value, filter)
  }

  function getGroups(filter: MessageHistoryFilter, excludeIds: string[] = []) {
    const excluded = new Set(excludeIds)
    const filtered = filterItems(items.value, filter).filter((item) => !excluded.has(item.id))

    return buildGroups(filtered)
  }

  return {
    items,
    loadingInitial,
    loadingMore,
    error,
    hasNext,
    eventId,
    totalCount,
    unreadThreadCount,
    unreadPulseCount,
    failedCount,
    pinnedCount,
    canLoadMore,
    refresh,
    fetchNext,
    getFilteredItems,
    getGroups,
  }
}
